/* global showError */

import axios from 'axios'
import { authStorage } from './authStorage'
import { tokenStore } from './tokenStore'
import { getCsrfToken } from './csrfToken'
import authConfig from 'src/configs/auth'
import { useToast } from 'src/@core/components/custom-Toast/customToast'

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete'])
const getAvailableEndpoints = () => {
  return [
    process.env.NEXT_PUBLIC_DEV_API_ENDPOINT_PORT_1,
    process.env.NEXT_PUBLIC_DEV_API_ENDPOINT_PORT_2,
    process.env.NEXT_PUBLIC_DEV_API_ENDPOINT_PORT_3,
    process.env.NEXT_PUBLIC_DEV_API_ENDPOINT_PORT_4,
    process.env.NEXT_PUBLIC_DEV_API_ENDPOINT_PORT_5
  ].filter(url => url && !url.startsWith('wss://'))
}

const getBaseURL = () => {
  const endpoints = getAvailableEndpoints()
  return endpoints.length > 0 ? endpoints[0] : process.env.NEXT_PUBLIC_API_URL
}

// Trades the HttpOnly refresh_token cookie for a new access token. Concurrent callers
// (e.g. several requests 401-ing at once) are coalesced onto a single in-flight
// request instead of firing one refresh call per request. Wrapped in Promise.resolve()
// so a synchronous throw (axios missing/misconfigured, as in some test doubles) still
// surfaces as a rejected promise rather than an uncaught exception.
//
// The backend never returns a token in this (or any auth) response body - success
// just means the access_token/refresh_token/csrf_token cookies were rotated. There is
// nothing to read out and reattach; every subsequent request picks the new cookies up
// automatically via withCredentials.
let refreshPromise = null

export const requestTokenRefresh = () => {
  if (!refreshPromise) {
    refreshPromise = Promise.resolve()
      .then(() =>
        axios.post(authConfig.refreshEndpoint, null, {
          withCredentials: true,
          headers: { 'X-CSRF-Token': getCsrfToken() ?? '' }
        })
      )
      .catch(error => {
        tokenStore.clear()
        throw error
      })
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

const createAPIClient = (logout, showError) => {
  const api = axios.create({
    baseURL: getBaseURL(),
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      // CDP endpoints return 406 without this on every request, uploads included.
      Accept: 'application/json'
    }
  })
  api.interceptors.request.use(
    config => {
      // Session travels entirely via the HttpOnly access_token cookie (withCredentials
      // above) - there is no token to attach as an Authorization header. Mutating
      // requests do need the readable csrf_token cookie echoed back as a header.
      if (MUTATING_METHODS.has((config.method || '').toLowerCase())) {
        config.headers['X-CSRF-Token'] = getCsrfToken() ?? ''
      }
      return config
    },
    error => Promise.reject(error)
  )

  api.interceptors.response.use(
    response => response,
    async error => {
      // A request the caller cancelled (AbortController) isn't a failure - no toast.
      if (error?.code === 'ERR_CANCELED' || axios.isCancel?.(error)) {
        return Promise.reject(error)
      }
      const originalRequest = error?.config
      const status = error?.response?.status
      // access_token's cookie Max-Age matches its own TTL, so once it naturally
      // expires the browser stops sending it entirely - the backend then sees no
      // cookie at all and responds 400 "Authentication token (Bearer token) is
      // missing from the request" (documented, consistent wording across every
      // endpoint), not 401. An actually-invalid-but-present token is 401. Both are
      // "please refresh" signals; a 400 that isn't this specific message is a
      // genuine validation error and should NOT trigger a wasted refresh call.
      const isMissingTokenError = status === 400 && /token/i.test(error?.response?.data?.message || '')

      // One retry attempt: silently refresh the access token and replay the request.
      // Only the real session-expired path below runs if the refresh itself fails.
      if ((status === 401 || isMissingTokenError) && originalRequest && !originalRequest._retriedAfterRefresh) {
        originalRequest._retriedAfterRefresh = true
        try {
          await requestTokenRefresh()
          // Refresh rotates csrf_token - re-read it for the retry if this was a
          // mutating request (the request interceptor won't run again on a manual replay).
          if (MUTATING_METHODS.has((originalRequest.method || '').toLowerCase())) {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers['X-CSRF-Token'] = getCsrfToken() ?? ''
          }
          return api(originalRequest)
        } catch {
          // Refresh failed too — fall through to the session-expired handling below.
        }
      }

      const capitalizeFirst = str => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str)
      // Opt-out for callers that render their own inline success/error status
      // (e.g. wizard Save flows) instead of the global toast. Session expiry
      // (401) always toasts regardless, since the page is about to navigate away.
      const skipGlobalToast = error?.config?.skipGlobalToast
      let message = 'Something went wrong. Please try again.'

      if (error.response) {
        let errorMessage = error?.response?.data?.message || error?.response?.data?.error
        if (errorMessage === 'segments.type.error.in.request') {
          errorMessage = 'Segment type is missing or invalid segment type'
        } else if (errorMessage === 'campaigns.audience.segment.id.required') {
          errorMessage = 'Please select at least one audience segment.'
        } else if (errorMessage === 'campaigns.invalid.segment.id') {
          errorMessage = 'One or more selected segments are invalid. Please reselect and try again.'
        } else if (errorMessage?.includes('media: upload validation failed:')) {
          const cleanMsg = errorMessage.replace('media: upload validation failed:', '').trim()
          errorMessage = cleanMsg.charAt(0).toUpperCase() + cleanMsg.slice(1)
        }

        if (
          error?.response?.data?.message?.includes(
            'duplicate key value violates unique constraint "audience_hashed_email_phone_external_id_key"'
          )
        ) {
          errorMessage = 'Audience already exists, please try with a different email or phone number.'
        } else if (
          error?.response?.data?.message?.includes('duplicate key value violates unique constraint "unique_audience"')
        ) {
          errorMessage = 'Email or phone number already present.'
        } else if (status === 401 || isMissingTokenError) {
          // Session expiry always toasts — never suppressed by skipGlobalToast.
          // Reached here only when a refresh was already attempted and failed too
          // (or wasn't attempted, e.g. no originalRequest) - a genuinely dead
          // session, whether the backend reported it as 401 or as this specific
          // "token missing" 400.
          message = errorMessage || 'Your session has expired. Please log in again.'
          showError(capitalizeFirst(message))
          logout()
          // authStorage.clearAuth?.()
          // window.location.href = '/login'
        } else if (status === 403) {
          message = errorMessage || 'You do not have permission to perform this action.'
          if (!skipGlobalToast) {
            showError(capitalizeFirst(message))
          }
        } else if (status === 404) {
          message = errorMessage || 'The requested resource was not found.'
          if (!skipGlobalToast) {
            showError(capitalizeFirst(message))
          }
        } else if (status === 400) {
          message = errorMessage || 'Bad request. Please check your input and try again.'
          if (!skipGlobalToast) {
            showError(capitalizeFirst(message))
          }
        } else if (status >= 500) {
          message = errorMessage || 'Server error. Please try again later.'
          if (!skipGlobalToast) {
            showError(capitalizeFirst(message))
          }
        } else {
          message = errorMessage || 'Error'
          if (!skipGlobalToast) {
            showError(capitalizeFirst(message))
          }
        }
      } else if (error.request) {
        message = 'Network error. Please check your connection.'
        if (!skipGlobalToast) {
          showError(capitalizeFirst(message))
        }
      }

      return Promise.reject(error)
    }
  )

  return api
}

export const useAPI = () => {
  const { showError } = useToast()
  const logout = () => {
    tokenStore.clear()
    authStorage.clearAuth?.()
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('logout', Date.now().toString())
      window.localStorage.setItem('showLogoutToast', 'true')
      try {
        window.location.href = '/login'
      } catch {
        // Safe catch for jsdom test environments where navigation is not implemented
      }
    }
  }

  return createAPIClient(logout, showError)
}
export function handleApiError(error, fallbackMessage = 'Something went wrong.') {
  // If interceptor normalized error, use it
  const normalizedMessage =
    error?.normalizedMessage || error?.response?.data?.message || error?.message || fallbackMessage

  showError(normalizedMessage)

  return normalizedMessage
}
